"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { ArrowRight, House } from "lucide-react";
const communities = [{
  name: "Naperville",
  description: "Top-rated schools, beautiful riverwalk, thriving downtown, and a strong sense of community.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_naperville_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_naperville_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_naperville_v3.png-wvc-sizes",
    alt: "Naperville Riverwalk"
  }
}, {
  name: "Plainfield",
  description: "Newer homes, growing neighborhoods, great schools, and plenty of shopping, dining, and family activities.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_plainfield_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_plainfield_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_plainfield_v3.png-wvc-sizes",
    alt: "Plainfield street view"
  }
}, {
  name: "St. Charles",
  description: "Charming riverfront community with a vibrant downtown, unique shops, great schools, and beautiful parks.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_st_charles_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_st_charles_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_st_charles_v3.png-wvc-sizes",
    alt: "St. Charles bridge"
  }
}, {
  name: "Schaumburg",
  description: "A dynamic suburb with top amenities, great schools, beautiful parks, shopping, dining, and easy access to highways.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_schaumburg_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_schaumburg_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_schaumburg_v3.png-wvc-sizes",
    alt: "Schaumburg modern architecture"
  }
}, {
  name: "Oswego",
  description: "Family-friendly living along the Fox River with new homes, top schools, parks, and a welcoming small-town feel.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_oswego_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_oswego_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_oswego_v3.png-wvc-sizes",
    alt: "Oswego riverfront"
  }
}, {
  name: "Wheaton",
  description: "Historic downtown with local shops, dining, and events, plus excellent schools and a welcoming community.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_wheaton_v3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_wheaton_v3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/community_wheaton_v3.png-wvc-sizes",
    alt: "Wheaton downtown"
  }
}];
export default function CommunitiesShowcase() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: isFooterVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="communities-showcase" className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-6 max-w-[1400px]">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="text-accent-gold font-bold tracking-[0.15em] uppercase text-xs md:text-sm mb-3 block">
            Explore The Western Suburbs
          </span>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-secondary tracking-tighter">
            Find Your Perfect Community.
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-16">
          {communities.map((community, index) => <div key={community.name} data-index={index} className={`transition-all duration-700 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${index * 100}ms`
        }}>
              <Card className="h-full overflow-hidden group border-none bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="relative overflow-hidden">
                  <AspectRatio ratio={4 / 5}>
                    <img src={community.image.src} data-wvc-srcset={community.image.srcset} data-wvc-sizes={community.image.sizes} alt={community.image.alt} loading="lazy" className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-700 ease-out" />
                  </AspectRatio>
                </div>
                <CardContent className="p-5 pt-4 flex flex-col h-full">
                  <h3 className="font-default text-lg font-bold text-secondary mb-2">
                    {community.name}
                  </h3>
                  <p className="text-[14px] text-muted-foreground leading-relaxed mb-6 flex-grow">
                    {community.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div ref={footerRef} className={`flex flex-col items-center gap-8 transition-all duration-1000 transform ${isFooterVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex items-center gap-3 text-secondary font-medium text-base md:text-lg text-center">
            <House className="w-6 h-6 text-accent-gold" />
            <p>Not sure which area is right for you? We can help!</p>
          </div>
          
          <Link to="#" className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-8 py-4 rounded-lg font-bold hover:bg-secondary/90 transition-all shadow-md group text-sm uppercase tracking-wide">
            Explore All Communities
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>;
}